import {
    CartShop,
    CouponGoodsItem,
    ISelectFullDiscount,
    PriceInfo,
    DiscountDetail
} from 'types/cart';
import { computeFullDiscount } from 'services/campaign/compute-full-discount';
import { ICouponApplied } from 'types/coupon';
import { computeMaximumCouponForGoodsList } from 'services/coupon/get-maximum-coupon';
import {
    computePlatformCouponBaseAmount,
    computeShopCouponBaseAmount
} from 'services/coupon/coupon-for-goods-list';
import { getCouponScope } from 'services/coupon/type';
import { couponScopeConst } from 'config/coupon';

function computeFullDiscountInCart(
    selectItem: CartShop[],
    selectedIds: string[],
    restrictIds?: string[]
) {
    const list: CouponGoodsItem[] = [];
    selectItem.forEach((shop) => {
        shop.items.forEach((goods) => {
            if (selectedIds.indexOf(goods.cartId) > -1) {
                list.push({
                    ...goods,
                    FCReducer: 0,
                    SCReducer: 0,
                    FDReducer: 0,
                    isLimit: (restrictIds || []).indexOf(goods.id) !== -1
                });
            }
        });
    });

    let originalTotal = 0;
    const selectFullDiscount: Record<string, ISelectFullDiscount> = {};
    let goods: Record<string, CouponGoodsItem[]> | undefined;

    // 商品初始化遍历
    list.forEach((item) => {
        if (!item.isLimit) {
            const price = item.price * item.amount;
            originalTotal += price;
            if (item.fullDiscount) {
                const id = item.fullDiscount.campaign_id;
                if (!selectFullDiscount[id]) {
                    selectFullDiscount[id] = {
                        list: [],
                        baseAmount: 0,
                        config: item.fullDiscount,
                        discount: 0,
                        desc: ''
                    };
                }
                selectFullDiscount[id].list.push(item);
                selectFullDiscount[id].baseAmount += price;
            }
        }
        goods = goods || {};
        if (goods[item.shopId]) {
            goods[item.shopId].push(item);
        } else {
            goods[item.shopId] = [item];
        }
    });

    // 满减计算
    let totalFullDiscount = 0;
    Object.values(selectFullDiscount).forEach((item) => {
        const base = computeFullDiscount(item.config, item.baseAmount);
        const discount = base.fullDiscount;
        item.desc = base.string;
        item.discount = discount;
        totalFullDiscount += discount;

        if (discount > 0) {
            let hasComputedDis = 0;
            const len = item.list.length;
            item.list
                .sort((item1, item2) => item1.price * item1.amount - item2.price * item2.amount)
                .forEach((goods, index) => {
                    if (index === len - 1) {
                        goods.FDReducer = discount - hasComputedDis;
                    } else {
                        const dis = Math.ceil(
                            ((goods.price * goods.amount) / item.baseAmount) * discount
                        );
                        goods.FDReducer = dis;
                        hasComputedDis += dis;
                    }
                });
        }
    });

    return {
        list,
        goods,
        selectFullDiscount,
        originalTotal,
        totalFullDiscount
    };
}

// 处理小于0元商品，获取selectState
function processUnderZeroGoods(list: CouponGoodsItem[]) {
    let realTotal = 0;
    list.forEach((goods) => {
        if (!goods.isLimit) {
            const realPrice =
                goods.price * goods.amount -
                (goods.FDReducer || 0) -
                (goods.SCReducer || 0) -
                (goods.FCReducer || 0);
            realTotal += realPrice <= 0 ? 1 : realPrice;
        }
    });

    return realTotal;
}

export const initComputeCartPrice = (
    selectedIds: string[],
    list: CartShop[],
    coupons: ICouponApplied[],
    needSelected?: boolean,
    restrictIds?: string[],
    priceInfo?: PriceInfo
) => {
    const { list: selectItem, goods } = computeFullDiscountInCart(list, selectedIds, restrictIds);
    if (selectItem.length === 0)
        return {
            realTotal: 0,
            originalTotal: 0,
            couponsDiscount: 0,
            totalFullDiscount: 0,
            selectedCoupon: {},
            available: coupons.filter((item) => getCouponScope(item) === couponScopeConst.platform),
            goods: Object.values(goods || { init: [] }).reduce(
                (item1, item2) => item1.concat(item2),
                []
            )
        };

    // 最佳优惠券
    let maxCoupon: {
        coupon?: Record<string, ICouponApplied | undefined>;
        discount: number;
        goods: CouponGoodsItem[];
        available: ICouponApplied[];
    } = {
        coupon: undefined,
        discount: 0,
        goods: [],
        available: []
    };
    if (goods && coupons.length > 0) {
        maxCoupon = computeMaximumCouponForGoodsList(coupons, goods, needSelected);
    } else if (goods) {
        maxCoupon.goods = [...selectItem];
    }

    return {
        realTotal: priceInfo?.total_price_info?.total_sku_price,
        originalTotal: priceInfo?.total_price_info.total_sum_price,
        couponsDiscount: priceInfo?.total_price_info.total_coupon_price,
        totalFullDiscount: priceInfo?.total_price_info.total_full_price,
        selectedCoupon: maxCoupon.coupon,
        available: maxCoupon.available,
        goods: maxCoupon.goods
    };
};

export const computePriceWithCoupon = (
    coupon: Record<string, ICouponApplied | undefined>,
    goodsList: CartShop[],
    selectedIds: string[],
    restrictIds?: string[]
) => {
    let list: CouponGoodsItem[] = [];

    const {
        goods,
        originalTotal,
        selectFullDiscount,
        totalFullDiscount
    } = computeFullDiscountInCart(goodsList, selectedIds, restrictIds);

    let couponDiscount = 0;
    let realTotal = originalTotal - totalFullDiscount;
    // 计算店铺券
    if (goods && Object.keys(coupon).length > 0) {
        Object.keys(goods).forEach((shopId) => {
            const base = computeShopCouponBaseAmount(goods[shopId], coupon[shopId]);
            couponDiscount += base.discount;
            list = list.concat(base.list);
        });
        // 计算平台券
        if (coupon.platform) {
            const base = computePlatformCouponBaseAmount(list, coupon.platform);
            couponDiscount += base.discount;
            list = base.list;
        }
        realTotal = processUnderZeroGoods(list);
    } else {
        list = Object.values(goods || { init: [] }).reduce(
            (item1, item2) => item1.concat(item2),
            []
        );
    }
    const fullDiscountItem = Object.values(selectFullDiscount)
        .filter((item) => item.discount > 0)
        .map((item) => {
            return {
                discount: item.discount,
                desc: item.desc
            };
        });

    return {
        goodsList: list,
        realTotal,
        originalTotal,
        couponsDiscount: originalTotal - realTotal - totalFullDiscount,
        totalFullDiscount,
        fullDiscountItem
    };
};

export const getFullDiscountItem = (priceInfo: PriceInfo) => {
    let result = [];
    let discountInfo: { [key: string]: number } = {};
    // let shopCampaign = {} as Record<string, DiscountDetail>;
    const shopsInfo = (priceInfo?.shop_sku_list || priceInfo?.shop_cart_list) ?? {}; // 价格接口返回 sku list。billinfo 下单接口返回 cart list
    const shopsFlatten = Object.keys(shopsInfo).map((key) => shopsInfo[key]);
    const platformFullDiscountInfo = priceInfo?.platform_discount_detail;
    const discountSum = platformFullDiscountInfo?.full_discount_amount;
    const fullDiscount = platformFullDiscountInfo?.full_discount_info?.full_discount.find(
        (item) => item.per_level === 1
    );
    if (discountSum && fullDiscount) {
        result.push({
            discount: discountSum,
            desc: `每满${fullDiscount?.full / 100}减${fullDiscount.discount / 100}`
        });
    }
    shopsFlatten.forEach((shop) => {
        const shopDiscountDetail = shop.shop_discount_detail;
        // const shopFullDiscount = shopDiscountDetail?.full_discount_info;
        // const campaignId = shopFullDiscount?.campaign_id;
        const discountSum = shopDiscountDetail?.full_discount_amount;
        const fullDiscounts = shopDiscountDetail?.full_discount_info?.select_shop_full_discount;
        fullDiscounts?.forEach((fullDiscount) => {
            if (!fullDiscount) {
                return;
            }
            const discountTitle = `满${fullDiscount.full / 100}减${fullDiscount.discount / 100}`;
            if (!discountInfo[discountTitle]) {
                discountInfo[discountTitle] = discountSum;
            } else {
                discountInfo[discountTitle] = discountInfo[discountTitle] + discountSum;
            }
        });
    });
    Object.keys(discountInfo).forEach((item) => {
        result.push({
            discount: discountInfo[item],
            desc: item
        });
    });
    return result;
};
